import { useState, useEffect, useRef } from 'react';
import { Download, Plus, X, ImagePlus, Loader2 } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Dropzone } from '@/components/ui/Dropzone';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { loadImage, readFileAsDataURL } from '@/lib/utils';

interface Frame {
  file: File;
  url: string;
  img: HTMLImageElement;
}

export function GifMaker() {
  const [frames, setFrames] = useState<Frame[]>([]);
  const [delay, setDelay] = useState(200);
  const [width, setWidth] = useState(480);
  const [loop, setLoop] = useState(true);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState(0);
  const [outUrl, setOutUrl] = useState('');
  const previewRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    return () => {
      frames.forEach((f) => URL.revokeObjectURL(f.url));
      if (outUrl) URL.revokeObjectURL(outUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(files: File[]) {
    const out: Frame[] = [];
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await readFileAsDataURL(f);
      const img = await loadImage(url);
      out.push({ file: f, url, img });
    }
    setFrames((p) => [...p, ...out]);
  }

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= frames.length) return;
    setFrames((arr) => {
      const next = arr.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  function remove(i: number) {
    setFrames((arr) => arr.filter((_, idx) => idx !== i));
  }

  async function build() {
    if (!frames.length) return;
    setWorking(true);
    setProgress(0);
    setOutUrl('');
    try {
      const GifModule = (await import('gif.js')).default as unknown as new (opts: Record<string, unknown>) => {
        on(event: string, handler: (...args: unknown[]) => void): void;
        addFrame(canvas: HTMLCanvasElement, opts?: Record<string, unknown>): void;
        render(): void;
      };
      const ratio = width / frames[0].img.width;
      const height = Math.round(frames[0].img.height * ratio);
      const gif = new GifModule({
        workers: 2,
        quality: 8,
        width,
        height,
        repeat: loop ? 0 : -1,
        workerScript: '/gif.worker.js',
      });
      const c = document.createElement('canvas');
      c.width = width;
      c.height = height;
      const ctx = c.getContext('2d')!;
      for (const f of frames) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.imageSmoothingQuality = 'high';
        // contain
        const r = Math.min(width / f.img.width, height / f.img.height);
        const dw = f.img.width * r;
        const dh = f.img.height * r;
        ctx.drawImage(f.img, (width - dw) / 2, (height - dh) / 2, dw, dh);
        gif.addFrame(c, { delay, copy: true });
      }
      await new Promise<void>((resolve, reject) => {
        gif.on('progress', (p) => setProgress(Math.round((p as number) * 100)));
        gif.on('finished', (blob) => {
          const url = URL.createObjectURL(blob as Blob);
          setOutUrl(url);
          resolve();
        });
        gif.on('error', () => reject());
        gif.render();
      });
      toast.success('GIF ready');
    } catch (e) {
      toast.error('GIF render failed');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="gif-maker"
        title="GIF maker"
        description="Combine images into an animated GIF with adjustable frame rate."
        badges={frames.length ? <Badge>{frames.length} frames</Badge> : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <Card>
            <Dropzone
              accept="image/*"
              multiple
              onFiles={handleFiles}
              icon={<ImagePlus size={18} />}
              hint="Add 2+ images — order = animation order"
            />
          </Card>

          {frames.length > 0 && (
            <Card className="!p-3">
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                {frames.map((f, i) => (
                  <div key={i} className="relative group rounded-md overflow-hidden border border-border">
                    <img src={f.url} alt={`frame ${i + 1}`} className="w-full h-20 object-cover" />
                    <div className="absolute top-0.5 left-0.5 px-1 text-2xs font-mono rounded bg-bg/80 backdrop-blur">
                      {i + 1}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 flex justify-between p-0.5 opacity-0 group-hover:opacity-100 transition">
                      <button
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        className="size-5 rounded bg-bg/80 backdrop-blur text-2xs disabled:opacity-30"
                      >
                        ←
                      </button>
                      <button
                        onClick={() => remove(i)}
                        className="size-5 rounded bg-danger/80 text-white flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                      <button
                        onClick={() => move(i, 1)}
                        disabled={i === frames.length - 1}
                        className="size-5 rounded bg-bg/80 backdrop-blur text-2xs disabled:opacity-30"
                      >
                        →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {outUrl && (
            <Card className="!p-3">
              <div className="text-xs font-medium text-fg-muted mb-2">Result</div>
              <img ref={previewRef} src={outUrl} alt="gif" className="max-w-full rounded shadow-card mx-auto" />
            </Card>
          )}
        </div>

        <Card className="space-y-3">
          <Field label="Width" hint={`${width}px`}>
            <Slider value={width} onChange={setWidth} min={120} max={1080} step={20} showValue={false} />
          </Field>
          <Field label="Frame delay" hint={`${delay}ms · ${(1000 / Math.max(1, delay)).toFixed(1)} fps`}>
            <Slider value={delay} onChange={setDelay} min={50} max={1000} step={10} showValue={false} />
          </Field>
          <label className="flex items-center justify-between text-sm cursor-pointer">
            <span className="text-fg-muted">Loop forever</span>
            <Switch checked={loop} onChange={setLoop} />
          </label>
          <Button
            variant="primary"
            className="w-full"
            disabled={!frames.length || working}
            loading={working}
            onClick={build}
          >
            <Plus size={11} />
            Build GIF
          </Button>
          {working && (
            <div>
              <div className="text-2xs text-fg-muted mb-1 flex items-center gap-1">
                <Loader2 size={10} className="animate-spin" />
                Encoding · {progress}%
              </div>
              <div className="h-1 bg-bg-inset rounded-full overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          {outUrl && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => fetch(outUrl).then((r) => r.blob()).then((b) => saveAs(b, 'animation.gif'))}
            >
              <Download size={11} />
              Save GIF
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
}

export default GifMaker;
